import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  composeWidget,
  createBoard,
  listBoards,
  moveWidget,
  previewWidgets,
  readBoard,
  readVocabulary,
  removeBoard,
  restoreBoard,
  saveBoard,
  type BoardSummary,
  type ComposeChoice,
  type Composed,
  type DashboardBody,
  type PreviewBody,
  type RemoveResult,
  type SaveResult,
  type Template,
  type Vocabulary,
  type WidgetDraft,
} from '@/features/mitra/api/dashboard'
import { dashboardKeys } from '@/features/dashboard/api/keys'
import { draftOf } from '@/features/dashboard/lib/formula'
import { config } from '@/lib/config'

/**
 * The reader's dashboards as React Query holds them.
 *
 * The engine calls never reject (`features/mitra/api/dashboard.ts`), so a
 * read that failed is turned into a thrown error here only for what a page
 * stands on -- the list and a board -- which `QueryState` draws as its failed
 * state; a preview, a composed widget or the vocabulary that could not be
 * read is simply `null`, since none is worth an error page.
 *
 * Gated on `config.mitraPrefix`: a build with no Mitra engine has no
 * dashboard to read, and asks nothing.
 */

/** Whether this build can reach the Mitra engine at all. */
export const dashboardsEnabled = (): boolean => config.mitraPrefix !== null

/** A page's own failure: the engine has no dashboards, the board is gone, or the read failed. */
export class DashboardUnavailable extends Error {
  constructor(readonly reason: 'unavailable' | 'missing' | 'failed') {
    super(
      reason === 'unavailable'
        ? 'Dashboards are not available'
        : reason === 'missing'
          ? 'That dashboard is no longer there'
          : 'The dashboard did not load',
    )
    this.name = 'DashboardUnavailable'
  }
}

export interface BoardsList {
  etag: string | null
  boards: BoardSummary[]
  templates: Template[]
}

/** The reader's dashboards by name, and the templates to start one from. */
export function useBoards(): UseQueryResult<BoardsList> {
  return useQuery({
    queryKey: dashboardKeys.boards(),
    queryFn: async () => {
      const read = await listBoards()
      if (read.kind === 'ok') return read
      throw new DashboardUnavailable(read.kind)
    },
    enabled: dashboardsEnabled(),
    staleTime: 0,
    retry: false,
  })
}

/** One dashboard's widgets with their values, worked out now. Fresh on every
 *  visit: a value from earlier is a value about an earlier book. */
export function useBoard(id: string | null = null): UseQueryResult<DashboardBody> {
  return useQuery({
    queryKey: dashboardKeys.board(id),
    queryFn: async () => {
      const read = await readBoard(id)
      if (read.kind === 'ok') return read.body
      throw new DashboardUnavailable(read.kind)
    },
    enabled: dashboardsEnabled(),
    staleTime: 0,
    retry: false,
  })
}

/** The reader's first dashboard (`/v1/dashboard`). */
export const useDashboard = (): UseQueryResult<DashboardBody> => useBoard(null)

/** What a formula may use, the measures and the templates. Stable for a session. */
export function useVocabulary(enabled = true): UseQueryResult<Vocabulary | null> {
  return useQuery({
    queryKey: dashboardKeys.vocabulary(),
    queryFn: readVocabulary,
    enabled: enabled && dashboardsEnabled(),
    staleTime: 5 * 60_000,
  })
}

/**
 * Widgets worked out without saving them. `enabled` false asks nothing;
 * the answer is per set of definitions (`dashboardKeys.preview`).
 */
export function usePreview(
  widgets: readonly WidgetDraft[],
  enabled = true,
): UseQueryResult<PreviewBody | null> {
  return useQuery({
    queryKey: dashboardKeys.preview(widgets),
    queryFn: () => previewWidgets(widgets),
    enabled: enabled && widgets.length > 0 && dashboardsEnabled(),
    staleTime: 30_000,
  })
}

/** A widget from the builder's choices, with its value now; null asks nothing. */
export function useCompose(choice: ComposeChoice | null): UseQueryResult<Composed | null> {
  return useQuery({
    queryKey: dashboardKeys.compose(choice ?? { measure: '', over: 'book' }),
    queryFn: () => (choice ? composeWidget(choice) : Promise.resolve(null)),
    enabled: choice !== null && dashboardsEnabled(),
    staleTime: 30_000,
  })
}

/** The value after it has stopped changing for `delayMs`: the formula box
 *  asks the engine once the reader pauses, not on every keystroke. */
export function useSettled<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value)
    }, delayMs)
    return () => {
      clearTimeout(timer)
    }
  }, [value, delayMs])
  return settled
}

export interface SaveVariables {
  etag: string | null
  widgets: readonly WidgetDraft[]
  name?: string | undefined
  purpose?: string | undefined
}

/**
 * Save one dashboard's whole list, conditional on the version shown. A saved
 * list replaces the board in the cache, so the screen draws what the engine
 * worked out for it; a conflict or a refusal comes back as the result for
 * the screen to say. The list of dashboards is re-read, since a name may have
 * changed.
 */
export function useSaveBoard(
  id: string | null = null,
): UseMutationResult<SaveResult, Error, SaveVariables> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ etag, widgets, name, purpose }: SaveVariables) =>
      saveBoard(id, etag, widgets, { name, purpose }),
    onSuccess: (result) => {
      if (result.kind !== 'saved') return
      queryClient.setQueryData(dashboardKeys.board(id), result.body)
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.boards() })
    },
  })
}

/** The first dashboard's save, as round one's screen called it. */
export const useSaveDashboard = (): UseMutationResult<SaveResult, Error, SaveVariables> =>
  useSaveBoard(null)

export interface CreateVariables {
  etag: string | null
  name?: string | undefined
  purpose?: string | undefined
  template?: string | undefined
  /** The company a one-company template is about. */
  symbol?: string | undefined
}

/** A new dashboard, empty or from a template; its board lands in the cache. */
export function useCreateBoard(): UseMutationResult<SaveResult, Error, CreateVariables> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createBoard,
    onSuccess: (result) => {
      if (result.kind !== 'saved') return
      const id = result.body.dashboard?.id
      if (id) queryClient.setQueryData(dashboardKeys.board(id), result.body)
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.boards() })
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.board(null) })
    },
  })
}

/** Remove a dashboard, conditional on the version shown. */
export function useRemoveBoard(): UseMutationResult<
  RemoveResult,
  Error,
  { id: string; etag: string | null }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, etag }) => removeBoard(id, etag),
    onSuccess: (result) => {
      if (result.kind === 'removed')
        void queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
    },
  })
}

/** Put a dashboard back as one of its kept versions left it. */
export function useRestoreBoard(
  id: string,
): UseMutationResult<SaveResult, Error, { etag: string | null; index: number }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ etag, index }) => restoreBoard(id, etag, index),
    onSuccess: (result) => {
      if (result.kind !== 'saved') return
      queryClient.setQueryData(dashboardKeys.board(id), result.body)
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.boards() })
    },
  })
}

/**
 * One widget moved to another dashboard, conditional on the version shown.
 * Both dashboards and the list are read again: the one it left and the one
 * it landed on both changed.
 */
export function useMoveWidget(
  id: string,
): UseMutationResult<SaveResult, Error, { widgetId: string; etag: string | null; toId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ widgetId, etag, toId }) => moveWidget(id, widgetId, etag, toId),
    onSuccess: (result, { toId }) => {
      if (result.kind !== 'saved') return
      queryClient.setQueryData(dashboardKeys.board(toId), result.body)
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.board(id) })
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.boards() })
    },
  })
}

/**
 * One widget added to a dashboard over HTTP, as a refused chat change's
 * "Add with the suggested fix" does (contract §7: add `suggested` with
 * `PUT /v1/dashboards/{id}`): the dashboard read now, the widget put at its
 * end, saved on the version just read. A save another tab beat is read and
 * tried once more; the engine's refusal comes back as it is.
 */
export async function addWidgetTo(id: string, draft: WidgetDraft): Promise<SaveResult> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const read = await readBoard(id)
    if (read.kind !== 'ok') return { kind: 'failed' }
    const result = await saveBoard(id, read.body.etag, [...read.body.widgets.map(draftOf), draft])
    if (result.kind !== 'conflict') return result
  }
  return { kind: 'conflict' }
}
